import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { doDelete, get, patch, post } from '../../../helpers/httpClient';
import { toast } from 'react-toastify';
import GalleryItemStructureEditor, { normalize, syncMobileProps } from '../../../helpers/GalleryItemStructureEditor';

function LayoutForm({ websiteId, initial, onSaved, onCancel }) {
  const isEdit = !!initial;
  const [name, setName] = useState(initial?.name ?? '');
  const [descriptionCut, setDescriptionCut] = useState(initial?.description_cut ?? 'none');
  const [columns, setColumns] = useState(initial ? { columns: initial.columns ?? [], mobile: initial.mobile_template ?? { properties: [] } } : { columns: [] });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Name is required.');
      return;
    }
    try {
      setSaving(true);
      const normalized = normalize(columns);
      const syncedMobileProps = syncMobileProps(normalized.columns, normalized.mobile.properties);
      const payload = { name: name.trim(), description_cut: descriptionCut, columns: normalized.columns, mobile: { properties: syncedMobileProps } };
      let result;
      if (isEdit) {
        result = await patch(`websites/${websiteId}/gallery-templates/${initial.id}`, payload);
      } else {
        result = await post(`websites/${websiteId}/gallery-templates`, payload);
      }
      onSaved(result.gallery_template);
    } catch {
      /* httpClient shows toast */
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit} className="mb-3">
      <Form.Group className="mb-3">
        <Form.Label>Name</Form.Label>
        <Form.Control
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Layout name"
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Description cut</Form.Label>
        <Form.Select value={descriptionCut} onChange={(e) => setDescriptionCut(e.target.value)}>
          <option value="none">don't cut</option>
          <option value="small">small</option>
          <option value="medium">medium</option>
        </Form.Select>
      </Form.Group>

      <GalleryItemStructureEditor value={columns} onChange={setColumns} />

      <div className="form-actions mt-3">
        <Button type="submit" className="primary-button" disabled={saving}>
          {saving ? <><Spinner animation="border" size="sm" className="me-2" />Saving...</> : 'Save'}
        </Button>
        <Button type="button" className="primary-button" onClick={onCancel} disabled={saving}>Cancel</Button>
      </div>
    </Form>
  );
}

function GalleryLayouts({ website }) {
  const websiteId = website.id;

  const [layouts, setLayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editingLayout, setEditingLayout] = useState(null);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await get(`websites/${websiteId}/gallery-templates`);
      setLayouts(resp?.gallery_templates ?? []);
    } catch {
      /* httpClient shows toast */
    } finally {
      setLoading(false);
    }
  }, [websiteId]);

  useEffect(() => { load(); }, [load]);

  const handleCreated = (layout) => {
    setLayouts((prev) => [...prev, layout]);
    setCreating(false);
  };

  const handleEdit = async (id) => {
    try {
      setLoadingEdit(true);
      const resp = await get(`websites/${websiteId}/gallery-templates/${id}`);
      setEditingLayout(resp.gallery_template);
    } catch {
      /* httpClient shows toast */
    } finally {
      setLoadingEdit(false);
    }
  };

  const handleUpdated = (layout) => {
    setLayouts((prev) => prev.map((t) => (t.id === layout.id ? layout : t)));
    setEditingLayout(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this gallery layout?')) return;
    try {
      setDeletingId(id);
      await doDelete(`websites/${websiteId}/gallery-templates/${id}`);
      setLayouts((prev) => prev.filter((t) => t.id !== id));
    } catch {
      /* httpClient shows toast */
    } finally {
      setDeletingId(null);
    }
  };

  if (loading || loadingEdit) {
    return <div className="text-center my-4"><Spinner animation="border" /></div>;
  }

  if (editingLayout) {
    return (
      <div>
        <div className="structure-header-row mb-3">
          <h5 className="mb-0">Gallery layouts</h5>
        </div>
        <LayoutForm
          websiteId={websiteId}
          initial={editingLayout}
          onSaved={handleUpdated}
          onCancel={() => setEditingLayout(null)}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="structure-header-row mb-3">
        <h5 className="mb-0">Gallery layouts</h5>
        {!creating && (
          <Button size="sm" type="button" className="primary-button m-0" onClick={() => setCreating(true)}>New layout</Button>
        )}
      </div>

      {creating && (
        <LayoutForm
          websiteId={websiteId}
          onSaved={handleCreated}
          onCancel={() => setCreating(false)}
        />
      )}

      {layouts.length === 0 && !creating && (
        <div className="gl-empty">No gallery layouts yet.</div>
      )}

      {!creating && layouts.map((t) => {
        const colCount = (t.columns ?? []).length;
        const propCount = (t.columns ?? []).reduce((s, c) => s + (c.properties ?? []).length, 0);
        return (
          <div key={t.id} className="gl-card">
            <div className="gl-card-info">
              <div className="gl-card-name">{t.name}</div>
              <div className="gl-card-meta">
                {colCount > 0 && (
                  <span className="gl-col-viz">
                    {(t.columns ?? []).map((col, i) => (
                      <span key={i} className="gl-col-bar" style={{ height: Math.max(8, Math.min(22, 4 + (col.properties ?? []).length * 4)) + 'px' }} />
                    ))}
                  </span>
                )}
                <span className="gl-meta-pill">{colCount} col{colCount !== 1 ? 's' : ''}</span>
                <span className="gl-meta-pill">{propCount} prop{propCount !== 1 ? 's' : ''}</span>
              </div>
            </div>
            <div className="gl-card-actions">
              <Button size="sm" type="button" className="primary-button m-0" onClick={() => handleEdit(t.id)}>Edit</Button>
              <button
                type="button"
                className="gl-delete-btn"
                onClick={() => handleDelete(t.id)}
                disabled={deletingId === t.id}
              >
                {deletingId === t.id ? <Spinner animation="border" size="sm" /> : 'Delete'}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default GalleryLayouts;
